import type { CommitmentsData, Locale, SectorDefinition, Theme } from '../types';
import { chrome, formatDataDate } from '../i18n';
import { PlatformBar } from '../components/PlatformBar';
import { AttentionInbox } from '../components/AttentionInbox';
import { icon } from '../components/Icon';
import {
  compositeScore,
  criticalCount,
  nationalIndex,
  nationalIndexPrev,
  statusFor,
  targetsAtRisk,
} from '../core/score';
import { buildAttention } from '../core/attention';
import { overallDelivery, programCounts } from '../core/programs';

interface Props {
  sectors: SectorDefinition[];
  commitments?: CommitmentsData;
  locale: Locale;
  theme: Theme;
}

/** Tier 0 — the PM cockpit: exception inbox first, then momentum, commitments, sectors. */
export function NationalView({ sectors, commitments, locale, theme }: Props): string {
  const c = chrome(locale);
  const index = nationalIndex(sectors, locale);
  const prev = nationalIndexPrev(sectors, locale);
  const delta = index - prev;
  const indexStatus = statusFor(index);
  const attention = buildAttention(sectors, commitments, locale);
  const counts = commitments ? programCounts(commitments.programs) : undefined;
  const delivered = commitments ? overallDelivery(commitments.programs) : 0;
  const latest = sectors.map((s) => s.updatedAt ?? '').sort().at(-1);

  const momentumClass = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const momentumIcon = delta > 0 ? 'trending-up' : delta < 0 ? 'trending-down' : 'minus';
  const momentumLabel = delta > 0 ? c.momentumUp : delta < 0 ? c.momentumDown : c.momentumFlat;

  const sectorCards = sectors
    .map((s) => {
      const content = s.content[locale];
      const score = compositeScore(s, locale);
      const status = statusFor(score);
      const risk = targetsAtRisk(s, locale);
      const crit = criticalCount(s, locale);
      return `
      <a class="card sector-card" style="--accent:${s.accent}" href="#/${s.id}" aria-label="${content.navLabel}">
        <div class="label">
          <span class="ic" style="background:${s.accent}22;color:${s.accent}">${icon(s.icon, 'icon icon-kpi')}</span>
          <b>${content.navLabel}</b>
          <span class="pill ${status}">${c.status[status]}</span>
        </div>
        <div class="score-row"><span class="val">${score}</span><span class="unit">/ 100</span></div>
        <div class="card-stats">
          <span class="${risk > 0 ? 'warn' : ''}">${icon('target', 'icon icon-xs')} ${risk} ${c.targetsAtRisk}</span>
          <span class="${crit > 0 ? 'crit' : ''}">${icon('alert-circle', 'icon icon-xs')} ${crit} ${c.criticalAlerts}</span>
        </div>
      </a>`;
    })
    .join('');

  const commitmentsSummary = counts
    ? `
    <a class="panel summary-panel" href="#/commitments">
      <h3 class="panel-title">${icon('flag')} <span>${c.commitmentsTitle}</span></h3>
      <div class="summary-grid">
        <div class="summary-big"><span class="val">${delivered}%</span><span class="unit">${c.delivery}</span></div>
        <div class="summary-counts">
          <span class="excellent">${counts['on-track']} ${c.onTrackCount}</span>
          <span class="watch">${counts['at-risk']} ${c.atRiskCount}</span>
          <span class="act">${counts['off-track']} ${c.offTrackCount}</span>
        </div>
      </div>
    </a>`
    : '';

  return `
    ${PlatformBar(sectors, 'national', locale, theme, Boolean(commitments), formatDataDate(locale, latest || undefined))}
    <section class="hero">
      <div class="hero-index ${indexStatus}">
        <div class="ring" style="--pct:${index}"><span class="ring-val">${index}</span><span class="ring-max">/100</span></div>
        <div class="hero-meta">
          <div class="eyebrow">${c.readinessIndex}</div>
          <span class="pill ${indexStatus} big">${c.status[indexStatus]}</span>
          <div class="momentum ${momentumClass}" title="${momentumLabel}">${icon(momentumIcon, 'icon icon-xs')} ${delta > 0 ? '+' : ''}${delta} <span>${momentumLabel}</span></div>
        </div>
      </div>
      <div class="hero-tiles">
        <div class="tile"><span>${c.targetsAtRisk}</span><b class="${sectors.reduce((a, s) => a + targetsAtRisk(s, locale), 0) ? 'warn' : ''}">${sectors.reduce((a, s) => a + targetsAtRisk(s, locale), 0)}</b></div>
        <div class="tile"><span>${c.criticalAlerts}</span><b class="${sectors.reduce((a, s) => a + criticalCount(s, locale), 0) ? 'crit' : ''}">${sectors.reduce((a, s) => a + criticalCount(s, locale), 0)}</b></div>
      </div>
    </section>

    <section class="grid cols">
      <div class="panel span7 attention-panel">
        <h3 class="panel-title">${icon('alert-triangle')} <span>${c.attentionTitle}</span></h3>
        ${AttentionInbox(attention, locale)}
      </div>
      <div class="span5">
        ${commitmentsSummary}
      </div>
    </section>

    <div class="section-title">${c.programsLabel ? '' : ''}</div>
    <section class="grid overview-cards" style="margin-top:16px">${sectorCards}</section>
    <div id="modal-root"></div>`;
}
